import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../utils/api';
import { Loader2, MapPin, Bed, Bath, Layers, Calendar, Building2, ArrowLeft } from 'lucide-react';

const TYPE_LABELS = {
  apartment: 'Апартамент', house: 'Къща', land: 'Парцел',
  office: 'Офис', commercial: 'Магазин', garage: 'Гараж'
};

const STATUS_LABELS = {
  available: 'Наличен', sold: 'Продаден', rented: 'Отдаден', reserved: 'Резервиран'
};

const resolveUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const PropertyDetail = () => {
  const { slug } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await apiClient.get(`/api/properties/slug/${slug}`, { skipAuth: true });
        setProperty(data);
        document.title = `${data.seo_title || data.title} — Deus Estate`;
        if (data.seo_description) {
          let meta = document.querySelector('meta[name="description"]');
          if (!meta) {
            meta = document.createElement('meta');
            meta.name = 'description';
            document.head.appendChild(meta);
          }
          meta.content = data.seo_description;
        }
      } catch {
        setProperty(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" /></div>;
  }
  if (!property) {
    return (
      <div className="min-h-screen pt-32 pb-20 container-luxury text-center">
        <h1 className="text-3xl font-serif text-[#F6F2E8] mb-4">Имотът не е намерен</h1>
        <Link to="/properties" className="text-[#D4AF37] hover:underline">← Назад към всички имоти</Link>
      </div>
    );
  }

  const allImages = [
    ...(property.featured_image ? [property.featured_image] : []),
    ...(property.gallery_images || []).filter(u => u !== property.featured_image)
  ];

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="property-detail">
      <div className="container-luxury">
        <Link to="/properties" className="inline-flex items-center gap-2 text-[#A9A9A9] hover:text-[#D4AF37] mb-6">
          <ArrowLeft size={16} /> <span>Всички имоти</span>
        </Link>

        {/* Image gallery */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          <div className="md:col-span-3 aspect-[16/9] bg-[#111111] rounded-2xl overflow-hidden border border-[#D4AF37]/10">
            {allImages.length > 0 ? (
              <img
                src={resolveUrl(allImages[activeImg])}
                alt={property.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.style.display = 'none';
                  const ph = e.currentTarget.parentElement.querySelector('[data-img-fallback]');
                  if (ph) ph.style.display = 'flex';
                }}
              />
            ) : null}
            {(allImages.length === 0) && (
              <div className="w-full h-full flex items-center justify-center"><Building2 className="w-20 h-20 text-[#D4AF37]/30" /></div>
            )}
            <div data-img-fallback className="absolute inset-0 hidden items-center justify-center"><Building2 className="w-20 h-20 text-[#D4AF37]/30" /></div>
          </div>
          {allImages.length > 1 && (
            <div className="md:col-span-3 grid grid-cols-4 md:grid-cols-8 gap-2">
              {allImages.slice(0, 8).map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 ${activeImg === i ? 'border-[#D4AF37]' : 'border-transparent'} transition-colors`}
                >
                  <img src={resolveUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded-full">{TYPE_LABELS[property.property_type] || property.property_type}</span>
                <span className="px-3 py-1 bg-[#111111] text-[#A9A9A9] text-xs rounded-full">{STATUS_LABELS[property.status] || property.status}</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-serif font-semibold text-[#F6F2E8] mb-3" data-testid="property-detail-title">{property.title}</h1>
              <div className="flex items-center gap-1 text-[#A9A9A9]">
                <MapPin size={16} /> <span>{property.city}{property.neighborhood ? `, ${property.neighborhood}` : ''}{property.address ? `, ${property.address}` : ''}</span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat icon={<Building2 size={18} />} label="Площ" value={`${property.area} м²`} />
              {property.bedrooms != null && <Stat icon={<Bed size={18} />} label="Спални" value={property.bedrooms} />}
              {property.bathrooms != null && <Stat icon={<Bath size={18} />} label="Бани" value={property.bathrooms} />}
              {property.floor != null && <Stat icon={<Layers size={18} />} label="Етаж" value={`${property.floor}${property.total_floors ? `/${property.total_floors}` : ''}`} />}
              {property.year_built != null && <Stat icon={<Calendar size={18} />} label="Година" value={property.year_built} />}
            </div>

            {property.short_description && (
              <p className="text-lg text-[#A9A9A9] leading-relaxed">{property.short_description}</p>
            )}

            {/* Description */}
            <div className="prose prose-invert max-w-none">
              <h2 className="text-2xl font-serif text-[#F6F2E8] mb-4">Описание</h2>
              <div className="text-[#A9A9A9] leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: property.description }} />
            </div>

            {/* Features */}
            {(property.features?.length > 0 || property.amenities?.length > 0) && (
              <div className="grid md:grid-cols-2 gap-6">
                {property.features?.length > 0 && (
                  <FeatureList title="Характеристики" items={property.features} />
                )}
                {property.amenities?.length > 0 && (
                  <FeatureList title="Удобства" items={property.amenities} />
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:sticky lg:top-28 self-start space-y-4">
            <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-2xl p-6">
              <p className="text-sm text-[#A9A9A9] mb-1">{property.transaction_type === 'rent' ? 'Месечна цена' : 'Цена'}</p>
              <p className="text-4xl font-bold text-[#D4AF37] mb-4" data-testid="property-detail-price">{(property.price || 0).toLocaleString()} €</p>
              <p className="text-xs text-[#A9A9A9] mb-6">≈ {Math.round((property.price || 0) / (property.area || 1)).toLocaleString()} €/м²</p>
              <Link to="/contact" className="btn-primary w-full text-center block">Запитване за имота</Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ icon, label, value }) => (
  <div className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl p-4 flex items-center gap-3">
    <div className="text-[#D4AF37]">{icon}</div>
    <div>
      <p className="text-xs text-[#A9A9A9]">{label}</p>
      <p className="text-sm font-semibold text-[#F6F2E8]">{value}</p>
    </div>
  </div>
);

const FeatureList = ({ title, items }) => (
  <div className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl p-5">
    <h3 className="font-serif font-semibold text-[#F6F2E8] mb-3">{title}</h3>
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="text-[#A9A9A9] text-sm flex items-start gap-2">
          <span className="text-[#D4AF37] mt-1">•</span> <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default PropertyDetail;
